import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import {
  getGetCollectionsForDepartmentQueryKey,
  getGetTravellersForDepartmentQueryKey,
  getGetTravellersPerOrganizationQueryKey,
  useGetTravellersForDepartmentHook,
  useGetTravellersPerOrganizationHook,
  useImportTravellers,
  useLinkTravellerBobcatAccount,
} from '@/api/trafikportalen';
import { FormSection } from '@/components/FormSection';
import { Loading } from '@/components/Loading';
import { BulkTravellerProgress } from '@/components/Travellers/BulkTravellerProgress';
import Button from '@/components/core/Button';
import { cn } from '@/helpers/classnames';
import { isResourceNotFoundError } from '@/helpers/apiError';
import { getTravellerDisplayName } from '@/helpers/bulkTravellerOperation';
import {
  downloadCSVTemplate,
  downloadExcelTemplate,
  getTravellerTemplateColumns,
} from '@/helpers/downloadTemplate';
import { parseFile, mapHeaders } from '@/helpers/fileParser';
import {
  TRAVELLER_FIELD_MAPPING,
  getTravellerImportBobcatLinks,
  processTravellerImport,
  resolveTravellerImportBobcatLink,
  toTravellerImportRowDTO,
  type TravellerImportPreview,
} from '@/helpers/travellerImport';
import { useBulkTravellerOperation } from '@/hooks/useBulkTravellerOperation';
import { toast } from '@/hooks/useToast';
import { IconCheck, IconDownload } from '@/icons';

interface TravellerImportFormProps {
  departmentId?: string;
  onOpenChange: (open: boolean) => void;
  onProcessingChange?: (processing: boolean) => void;
}

export const TravellerImportForm = ({
  departmentId,
  onOpenChange,
  onProcessingChange = () => undefined,
}: TravellerImportFormProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const importTravellers = useImportTravellers();
  const linkTravellerBobcatAccount = useLinkTravellerBobcatAccount();
  const getTravellersPerOrganization = useGetTravellersPerOrganizationHook();
  const getTravellersForDepartment = useGetTravellersForDepartmentHook();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [parsedData, setParsedData] = useState<TravellerImportPreview[] | null>(
    null,
  );

  const {
    isProcessing,
    isComplete,
    processedCount,
    progressTotal,
    currentName,
    results,
    remainingTimeLabel,
    showRemainingTime,
    run,
  } = useBulkTravellerOperation({
    onProcessingChange,
    onComplete: () => undefined,
  });

  const {
    getRootProps,
    getInputProps,
    fileRejections,
    isDragActive,
    inputRef,
  } = useDropzone({
    onDrop: (incomingFiles) => {
      setSelectedFile(incomingFiles[0]);
      handleFileSelect(incomingFiles[0]);
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const handleFileSelect = useCallback(async (file: File) => {
    setIsParsing(true);
    try {
      const { data, headers } = await parseFile(file);
      const headerMap = mapHeaders(headers, TRAVELLER_FIELD_MAPPING);

      const results = processTravellerImport(data, headerMap);

      setParsedData(results);
    } catch (error) {
      console.error('Error parsing file:', error);
      setParsedData(null);
    } finally {
      setIsParsing(false);
    }
  }, []);

  const invalidateTravellerQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetTravellersPerOrganizationQueryKey(),
      }),
      departmentId
        ? queryClient.invalidateQueries({
            queryKey: getGetTravellersForDepartmentQueryKey(departmentId),
          })
        : Promise.resolve(),
      departmentId
        ? queryClient.invalidateQueries({
            queryKey: getGetCollectionsForDepartmentQueryKey(departmentId),
          })
        : Promise.resolve(),
    ]);
  }, [departmentId, queryClient]);

  const fetchImportedTravellers = useCallback(async () => {
    if (departmentId) {
      return queryClient.fetchQuery({
        queryKey: getGetTravellersForDepartmentQueryKey(departmentId),
        queryFn: ({ signal }) =>
          getTravellersForDepartment(departmentId, signal),
      });
    }

    return queryClient.fetchQuery({
      queryKey: getGetTravellersPerOrganizationQueryKey(),
      queryFn: ({ signal }) => getTravellersPerOrganization(signal),
    });
  }, [
    departmentId,
    getTravellersForDepartment,
    getTravellersPerOrganization,
    queryClient,
  ]);

  const handleConfirmImport = useCallback(async () => {
    if (!parsedData) {
      return;
    }

    const validResults = parsedData.filter(
      (result) => result.status !== 'invalid',
    );

    if (validResults.length === 0) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Inga giltiga resenärer att importera',
          }),
        },
        { autoClose: 3000 },
      );
      return;
    }

    setIsImporting(true);
    onProcessingChange(true);

    const finishWithoutLinking = () => {
      setIsImporting(false);
      onProcessingChange(false);
      onOpenChange(false);
    };

    let response;
    try {
      response = await importTravellers.mutateAsync({
        data: {
          ...(departmentId ? { departmentID: departmentId } : {}),
          travellers: validResults.map((result) =>
            toTravellerImportRowDTO(result.data),
          ),
        },
      });
    } catch (error) {
      console.error('Error importing travellers:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Ett fel uppstod vid import av resenärer',
          }),
        },
        { autoClose: 3000 },
      );
      setIsImporting(false);
      onProcessingChange(false);
      return;
    }

    await invalidateTravellerQueries();

    const successCount = response.successful;
    const errorCount =
      response.failed +
      parsedData.filter((result) => result.status === 'invalid').length;

    if (successCount > 0) {
      toast.success(
        {
          title: formatMessage(
            {
              defaultMessage:
                '{successCount, plural, one {# resenär importerad} other {# resenärer importerade}}.',
            },
            { successCount },
          ),
        },
        { autoClose: 3000 },
      );
    }

    if (errorCount > 0) {
      const failureMessages = response.failures
        .slice(0, 5)
        .map(
          (failure) =>
            `Rad ${failure.row_number}: ${failure.reasons.join(', ')}`,
        )
        .join('\n');

      toast.error(
        {
          title: formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# resenär} other {# resenärer}} kunde inte importeras',
            },
            { count: errorCount },
          ),
          description: failureMessages,
        },
        { autoClose: 5000 },
      );
    }

    const links = getTravellerImportBobcatLinks(
      validResults,
      response.failures.map((failure) => failure.data.email),
    );

    if (links.length === 0) {
      finishWithoutLinking();
      return;
    }

    let travellers;
    try {
      travellers = await fetchImportedTravellers();
    } catch (error) {
      console.error('Failed to load travellers after import:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage:
              'Kunde inte koppla E-post i appen / Mina sidor. Försök igen från resenärens profil.',
          }),
        },
        { autoClose: 4000 },
      );
      finishWithoutLinking();
      return;
    }

    const items = links.flatMap((link) => {
      const match = resolveTravellerImportBobcatLink(travellers ?? [], link);
      if (match?.alreadyLinked) {
        return [];
      }

      return [
        {
          link,
          match,
        },
      ];
    });

    if (items.length === 0) {
      finishWithoutLinking();
      return;
    }

    setIsLinking(true);
    setIsImporting(false);

    const notFoundMessage = formatMessage({
      defaultMessage: 'Resenären hittades inte efter importen',
    });
    const noAccountMessage = formatMessage({
      defaultMessage:
        'Det finns inget konto i reseappen med denna e-postadress.',
    });

    try {
      await run({
        items,
        getItem: ({ link, match }) => ({
          id: match?.travellerId ?? link.email,
          name: getTravellerDisplayName(
            match?.name ?? {
              first_name: link.firstName,
              last_name: link.lastName,
            },
            link.email,
          ),
        }),
        execute: async ({ match }) => {
          if (!match) {
            throw new Error(notFoundMessage);
          }

          try {
            await linkTravellerBobcatAccount.mutateAsync({
              travellerID: match.travellerId,
              data: { bobcat_private_email: match.bobcatPrivateEmail },
            });
          } catch (error) {
            if (isResourceNotFoundError(error)) {
              throw new Error(noAccountMessage);
            }
            throw error;
          }
        },
        afterAll: invalidateTravellerQueries,
        showCompletionToasts: false,
      });
    } catch (error) {
      console.error('Failed to link bobcat accounts after import:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage:
              'Kunde inte koppla E-post i appen / Mina sidor. Försök igen från resenärens profil.',
          }),
        },
        { autoClose: 4000 },
      );
      onProcessingChange(false);
    }
  }, [
    departmentId,
    fetchImportedTravellers,
    formatMessage,
    importTravellers,
    invalidateTravellerQueries,
    linkTravellerBobcatAccount,
    onOpenChange,
    onProcessingChange,
    parsedData,
    run,
  ]);

  const handleClose = useCallback(() => {
    if (isProcessing || isImporting) {
      return;
    }
    onOpenChange(false);
  }, [isImporting, isProcessing, onOpenChange]);

  const renderFileRejection = useCallback(() => {
    return fileRejections.map(({ file }) => (
      <div
        className="flex w-full flex-col items-center justify-center"
        key={file.path}
      >
        <span className="text-red-800">
          {formatMessage(
            { defaultMessage: '{fileName} är inte ett giltligt filformat' },
            { fileName: file.name },
          )}
        </span>
        <span className="text-red-600">
          <FormattedMessage defaultMessage="Endast CSV och Excel (.xlsx) filer är tillåtna" />
        </span>
      </div>
    ));
  }, [fileRejections, formatMessage]);

  const renderFileStatus = useCallback(() => {
    if (selectedFile && parsedData) {
      return (
        <div className="flex items-center gap-2">
          <IconCheck className="size-5 text-green-600" />
          <span className="font-medium">{selectedFile.name}</span>
        </div>
      );
    }
    if (selectedFile) {
      return <span>{selectedFile.name}</span>;
    }
    return (
      <span className="text-text-700">
        {formatMessage({
          defaultMessage:
            'Dra och släpp eller klicka för att välja fil för import',
        })}
      </span>
    );
  }, [selectedFile, parsedData, formatMessage]);

  const handleDownloadCSVTemplate = useCallback(() => {
    const columns = getTravellerTemplateColumns(!departmentId);
    downloadCSVTemplate('resenarer_mall.csv', columns);
  }, [departmentId]);

  const handleDownloadExcelTemplate = useCallback(async () => {
    const columns = getTravellerTemplateColumns(!departmentId);
    await downloadExcelTemplate('resenarer_mall.xlsx', columns);
  }, [departmentId]);

  if (isParsing || (isImporting && !isLinking)) {
    return <Loading />;
  }

  if (isLinking || isProcessing || isComplete) {
    return (
      <BulkTravellerProgress
        isProcessing={isProcessing}
        processedCount={processedCount}
        progressTotal={progressTotal}
        currentName={currentName}
        remainingTimeLabel={remainingTimeLabel}
        showRemainingTime={showRemainingTime}
        results={results}
        progressAriaLabel={formatMessage({
          defaultMessage: 'Förlopp för koppling av e-post i appen / Mina sidor',
        })}
        stayOnPageMessage={
          <FormattedMessage defaultMessage="Lämna inte sidan. Kopplingen av e-post i appen / Mina sidor görs en resenär i taget i webbläsaren och avbryts om du navigerar bort." />
        }
        successSummary={(count) => (
          <FormattedMessage
            defaultMessage="{count, plural, one {# resenär kopplades till e-post i appen / Mina sidor} other {# resenärer kopplades till e-post i appen / Mina sidor}}"
            values={{ count }}
          />
        )}
        failureSummary={(count) => (
          <FormattedMessage
            defaultMessage="{count, plural, one {# resenär kunde inte kopplas till e-post i appen / Mina sidor} other {# resenärer kunde inte kopplas till e-post i appen / Mina sidor}}"
            values={{ count }}
          />
        )}
        onClose={handleClose}
      />
    );
  }

  return (
    <form className="flex size-full flex-col gap-4">
      <FormSection
        title={formatMessage({ defaultMessage: 'Välj fil att importera' })}
      >
        <div
          {...getRootProps({
            className: cn(
              'flex border border-dashed border-2 border-background-400 rounded-md p-8 items-center gap-4 flex-col hover:bg-background-100 cursor-pointer',
              isDragActive && 'bg-background-100',
            ),
          })}
        >
          <input ref={inputRef} {...getInputProps()} aria-label="Välj fil" />
          {renderFileStatus()}
          {renderFileRejection()}
        </div>
        <p className="mt-2 text-sm text-text-700">
          <FormattedMessage defaultMessage="Tillåtna filformat: CSV (.csv), Excel (.xlsx)" />
        </p>
        <div className="mt-3 rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
          <strong>
            <FormattedMessage defaultMessage="Observera:" />
          </strong>
          <ul className="ml-4 mt-1 list-disc space-y-1">
            <li>
              <FormattedMessage defaultMessage="Resenärer som redan finns (matchas på e-postadress) kommer att uppdateras med informationen från filen. Nya resenärer läggs till automatiskt." />
            </li>
            <li>
              <FormattedMessage defaultMessage="'E-post i appen / Mina sidor' kopplas efter att importen är klar, en resenär i taget." />
            </li>
            {!departmentId && (
              <li>
                <FormattedMessage defaultMessage="Om det i organisationen finns flera avdelningar med samma namn kommer systemet att välja den första matchningen." />
              </li>
            )}
          </ul>
        </div>
      </FormSection>

      <FormSection
        title={formatMessage({ defaultMessage: 'Kolumner i filen' })}
      >
        <div className="text-sm text-text-700">
          <p className="mb-2">
            <FormattedMessage defaultMessage="Filen måste innehålla följande kolumner (kolumnnamnen tillåts variera något, men rekommenderat är att hålla sig till nedan):" />
          </p>
          <ul className="ml-4 list-disc space-y-1">
            {getTravellerTemplateColumns(false).map((column) => (
              <li key={column.header}>
                <p>
                  {column.header}
                  {column.header === 'E-post' ? (
                    <span className="text-text-500">
                      {' '}
                      (<FormattedMessage defaultMessage="obligatorisk" />)
                    </span>
                  ) : null}
                </p>
              </li>
            ))}
            {!departmentId && (
              <li>
                <FormattedMessage defaultMessage="Avdelning (skiftlägeskänsligt)" />
              </li>
            )}
          </ul>
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            variant="outline"
            startIcon={<IconDownload />}
            onClick={handleDownloadCSVTemplate}
          >
            <FormattedMessage defaultMessage="Ladda ner CSV-mall" />
          </Button>
          <Button
            variant="outline"
            startIcon={<IconDownload />}
            onClick={handleDownloadExcelTemplate}
          >
            <FormattedMessage defaultMessage="Ladda ner Excel-mall" />
          </Button>
        </div>
      </FormSection>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="ghost" onClick={handleClose} className="mr-auto">
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        {selectedFile && (
          <Button
            variant="ghost"
            onClick={() => {
              setParsedData(null);
              setSelectedFile(null);
              inputRef.current?.click();
            }}
          >
            <FormattedMessage defaultMessage="Välj ny fil" />
          </Button>
        )}
        {selectedFile && parsedData && (
          <Button
            onClick={handleConfirmImport}
            variant="filled"
            startIcon={<IconCheck />}
          >
            <FormattedMessage defaultMessage="Importera" />
          </Button>
        )}
      </div>
    </form>
  );
};
